/**
 * CLIENT-SIDE RATE LIMITING UTILITIES
 *
 * Implements sliding window rate limiting to prevent users from overwhelming
 * the application with excessive requests. Provides UX-level protection
 * (not security - users can bypass client-side limits).
 *
 * Risk-Based Usage:
 * - 🔴 Critical operations: 3 per minute (account deletion, sensitive changes)
 * - 🟠 High risk operations: 5 per minute (form submissions, creation/updates)
 * - 🟡 Medium risk operations: 15 per minute (password validation, complex queries)
 * - 🟢 Low risk operations: 30 per minute (simple data fetching)
 * - 📊 Bulk reads: 60 per minute (dashboard data, analytics)
 *
 * UTILITIES:
 * ├── ClientRateLimiter ← Rate limiting class
 * ├── withRateLimit() ← Higher-order function wrapper
 * └── rateLimiters ← Pre-configured instances
 */

import {
  LEGACY_RATE_LIMITERS,
  RATE_LIMITERS,
} from '../constants/rateLimit.constants';

/**
 * CLIENT-SIDE RATE LIMITING CLASS
 *
 * Implements sliding window rate limiting to prevent users from overwhelming
 * the application with excessive requests. Useful for form submissions,
 * API calls, and other user-initiated actions.
 */
export class ClientRateLimiter {
  private requests: Map<string, number[]> = new Map();

  constructor(
    private maxRequests: number = 10, // Maximum requests allowed
    private windowMs: number = 60000, // Time window in milliseconds (1 minute)
  ) {}

  /**
   * Checks if a request should be allowed based on current rate limits
   */
  isAllowed(key: string): boolean {
    const now = Date.now();
    const requests = this.requests.get(key) || [];

    // Remove requests outside the current time window
    const validRequests = requests.filter((time) => now - time < this.windowMs);

    if (validRequests.length >= this.maxRequests) {
      return false; // Rate limit exceeded
    }

    // Record this request
    validRequests.push(now);
    this.requests.set(key, validRequests);

    return true;
  }

  /**
   * Resets rate limit for a specific key
   */
  reset(key: string): void {
    this.requests.delete(key);
  }
}

/**
 * PRE-CONFIGURED RATE LIMITERS - RISK-BASED APPROACH
 *
 * Global rate limiters for different risk levels of operations.
 * These instances are configured based on the risk-based security constants.
 */
export const rateLimiters = {
  // 🔴 CRITICAL OPERATIONS - Most restrictive
  criticalOperations: new ClientRateLimiter(
    RATE_LIMITERS.CRITICAL_OPERATIONS.maxRequests,
    RATE_LIMITERS.CRITICAL_OPERATIONS.windowMs,
  ),

  // 🟠 HIGH RISK OPERATIONS - Strict limits
  highRisk: new ClientRateLimiter(
    RATE_LIMITERS.HIGH_RISK.maxRequests,
    RATE_LIMITERS.HIGH_RISK.windowMs,
  ),

  // 🟡 MEDIUM RISK OPERATIONS - Moderate limits
  mediumRisk: new ClientRateLimiter(
    RATE_LIMITERS.MEDIUM_RISK.maxRequests,
    RATE_LIMITERS.MEDIUM_RISK.windowMs,
  ),

  // 🟢 LOW RISK OPERATIONS - Lenient limits
  lowRisk: new ClientRateLimiter(
    RATE_LIMITERS.LOW_RISK.maxRequests,
    RATE_LIMITERS.LOW_RISK.windowMs,
  ),

  // 📁 FILE OPERATIONS - Special handling
  fileUploads: new ClientRateLimiter(
    RATE_LIMITERS.FILE_UPLOADS.maxRequests,
    RATE_LIMITERS.FILE_UPLOADS.windowMs,
  ),

  // 📊 BULK READS - Very permissive for UI performance
  bulkReads: new ClientRateLimiter(
    RATE_LIMITERS.BULK_READS.maxRequests,
    RATE_LIMITERS.BULK_READS.windowMs,
  ),

  // BACKWARD COMPATIBILITY - Legacy aliases
  formSubmissions: new ClientRateLimiter(
    LEGACY_RATE_LIMITERS.FORM_SUBMISSION.maxRequests,
    LEGACY_RATE_LIMITERS.FORM_SUBMISSION.windowMs,
  ),
  apiCalls: new ClientRateLimiter(
    LEGACY_RATE_LIMITERS.API_CALLS.maxRequests,
    LEGACY_RATE_LIMITERS.API_CALLS.windowMs,
  ),
};

// Type exports for better type safety
export type RateLimiterType = keyof typeof rateLimiters;

/**
 * RATE LIMITING WRAPPER
 *
 * Higher-order function that wraps any async function with rate limiting.
 * This provides consistent rate limiting across all gateway methods.
 *
 * @param fn - The async function to wrap
 * @param limitType - Type of rate limiter to use
 * @param key - Unique key for this specific operation
 * @param customMessage - Optional custom error message
 * @returns Wrapped function with rate limiting
 *
 */
export function withRateLimit<T extends (...args: any[]) => Promise<any>>(
  fn: T,
  limitType: keyof typeof rateLimiters,
  key: string,
  customMessage?: string,
): T {
  return (async (...args: Parameters<T>) => {
    // Check rate limit before executing function
    if (!rateLimiters[limitType].isAllowed(key)) {
      const defaultMessages = {
        criticalOperations:
          'Too many critical operations. Please wait before trying again.',
        highRisk:
          'Too many form submissions. Please wait a moment before trying again.',
        mediumRisk: 'Too many requests. Please wait a moment.',
        lowRisk: 'Too many requests. Please slow down.',
        fileUploads:
          'Too many file uploads. Please wait before uploading again.',
        bulkReads: 'Too many data requests. Please wait a moment.',
        // Legacy messages
        formSubmissions:
          'Too many form submissions. Please wait a moment before trying again.',
        apiCalls: 'Too many API requests. Please wait a moment.',
      };

      throw new Error(customMessage || defaultMessages[limitType]);
    }

    // Execute the original function if rate limit allows
    return fn(...args);
  }) as T;
}
